import type { Dayjs } from "dayjs"

import type { TrackedRecipient } from "../types/tracking.types.ts"
import { secondsBetween } from "./api-time.ts"
import { isCountersigner } from "./countersigner.ts"

/**
 * One recipient's state, as the Recipients cell says it. Time to first open and
 * time to sign are different diagnoses (R15.3b), so the line says which one it
 * is. Every clock runs from this recipient's own send, never the envelope's.
 */
export type RecipientLine =
  | { kind: "not_sent" }
  | { kind: "declined" }
  | { kind: "bounced" }
  | { kind: "signed"; seconds: number | null }
  | { kind: "countersigned"; seconds: number | null }
  | { kind: "waiting_on_us"; seconds: number | null }
  | { kind: "opened"; opens: number; seconds: number | null }
  | { kind: "stuck_at_code"; seconds: number | null }
  | { kind: "not_clicked"; seconds: number | null }

export const recipientLine = (
  recipient: TrackedRecipient,
  now: Dayjs
): RecipientLine => {
  const { activity } = recipient
  if (recipient.status === "declined") return { kind: "declined" }
  if (!activity.first_sent) return { kind: "not_sent" }
  // The countersignature gets no link and its in-app reads record no open.
  if (isCountersigner(recipient)) {
    return recipient.signed
      ? {
          kind: "countersigned",
          seconds: secondsBetween(activity.first_sent, recipient.signed)
        }
      : {
          kind: "waiting_on_us",
          seconds: secondsBetween(activity.first_sent, now)
        }
  }
  if (recipient.signed) {
    return {
      kind: "signed",
      seconds: secondsBetween(activity.first_sent, recipient.signed)
    }
  }
  if (activity.last_bounced && !activity.first_opened)
    return { kind: "bounced" }
  if (activity.first_opened) {
    return {
      kind: "opened",
      opens: activity.opens,
      seconds: secondsBetween(activity.first_sent, activity.first_opened)
    }
  }
  const waited = secondsBetween(activity.first_sent, now)
  return activity.first_link_opened
    ? { kind: "stuck_at_code", seconds: waited }
    : { kind: "not_clicked", seconds: waited }
}
